import type {
  Query,
  QueryMeta,
  QueryObserverResult,
  RefetchOptions,
} from '@tanstack/react-query';
import type { TRPCClientErrorLike } from '@trpc/client';
import { isTRPCClientError } from '@trpc/client';

import { isPageUnloading } from './pageUnload';

/**
 * Query freshness windows (COI-882). These live here rather than in `trpc.ts`
 * so a hook can import one without pulling the QueryClient module in with it —
 * that import is what breaks module mocks in the web test setup.
 *
 * Prod measurement behind the numbers: 2,171 tRPC requests in one hour resolved
 * to 232 distinct queries. 89% of production traffic was an exact repeat of a
 * query already made, and half of ALL traffic was 16 unchanging queries
 * re-fetched on every navigation.
 */

/**
 * Default freshness for every query — a dedupe window, not a cache policy.
 * See the `staleTime` note in `trpc.ts` for why it is seconds and not
 * milliseconds.
 */
export const DEDUPE_WINDOW_MS = 10_000;

/**
 * Register/badge counts (`useGetIssueCount` and its 18 siblings, rendered
 * through `ConnectedCount`).
 *
 * Measured at ~70 calls per hour EACH with a single distinct input — the same
 * count, recomputed on every navigation. A count is an aggregate over a
 * register: it changes when someone creates or deletes a record, and the
 * mutation hooks already invalidate the count query families for the actor who
 * did it (see the cache-invalidation-parity rule in the package CLAUDE.md). So
 * this window only governs how quickly a badge notices ANOTHER user's write,
 * where a minute is not a meaningful delay on a governance register.
 */
export const COUNT_STALE_TIME_MS = 60_000;

/**
 * Org-level reference data — departments, the user directory, entity/register
 * definitions, form customisation. Administrative config that changes on the
 * order of days, re-fetched on every navigation today (`department.allTypes`
 * and `user.directory` each ~77 calls/hour against ONE distinct input).
 *
 * Writes to these go through mutation hooks that invalidate their own query
 * family, so an admin editing a department still sees it immediately; this
 * window only delays how fast OTHER sessions pick the change up.
 */
export const REFERENCE_DATA_STALE_TIME_MS = 5 * 60_000;

const fetchAbortErrorMessages = [
  'Failed to fetch',
  'Load failed',
  'NetworkError when attempting to fetch resource.',
];

const isAbortLikeObject = (error: unknown): boolean =>
  typeof error === 'object' &&
  error !== null &&
  'name' in error &&
  error.name === 'AbortError';

const hasFetchAbortError = (error: unknown, isCause = false): boolean => {
  if (isCause && isAbortLikeObject(error)) {
    return true;
  }

  if (!(error instanceof Error)) {
    return false;
  }

  if (hasFetchAbortError(error.cause, true)) {
    return true;
  }

  if (
    error.name === 'AbortError' ||
    fetchAbortErrorMessages.some((message) => error.message.includes(message))
  ) {
    return true;
  }

  return false;
};

/**
 * A tRPC failure that is a fetch-level abort / network teardown rather than a
 * response the server actually sent. Says nothing about *why* the fetch died —
 * `shouldReportTrpcQueryError` decides whether the cause was ours.
 */
export const isTrpcFetchAbortError = (error: unknown): boolean =>
  isTRPCClientError(error) && hasFetchAbortError(error);

/**
 * The server's inbound rate limiter rejected the call (COI-454 / COI-882).
 */
export const isTrpcRateLimitError = (error: unknown): boolean =>
  isTRPCClientError(error) && error.data?.code === 'TOO_MANY_REQUESTS';

/**
 * Default `retry` for every query (wired in `utils/trpc.ts`).
 *
 * Retrying a rate-limited request makes the condition worse: each retry spends
 * another point against the same budget, so a user who crosses the limit cannot
 * get back under it by waiting out the backoff — react-query's default of 3
 * retries turns one rejected query into four. With batching off (COI-451) a
 * single navigation is already hundreds of requests, so the amplification is
 * what turned a brief overrun into a page that never recovered (COI-882).
 *
 * Everything else keeps the library default of 3 attempts.
 */
export const DEFAULT_QUERY_RETRY_COUNT = 3;

export const shouldRetryTrpcQuery = (
  failureCount: number,
  error: unknown
): boolean => {
  if (isTrpcRateLimitError(error)) {
    return false;
  }

  return failureCount < DEFAULT_QUERY_RETRY_COUNT;
};

/**
 * Spread into a query's options to opt it out of the global Sentry reporting in
 * `QueryCache.onError` (src/utils/trpc.ts):
 *
 * ```ts
 * trpc.frontend.g2Review.promptState.queryOptions(undefined, {
 *   ...bestEffortQueryMeta,
 *   retry: false,
 * })
 * ```
 *
 * Only for genuinely fire-and-forget reads — a query whose failure is already
 * handled by rendering nothing, where the user is neither blocked nor told, so
 * a Sentry event is pure noise (COI-716). Every other query keeps reporting;
 * do NOT reach for this to quieten a failure someone should be looking at.
 */
export const bestEffortQueryMeta = {
  meta: { reportErrors: false },
} satisfies { meta: QueryMeta };

/**
 * Whether the failed query opted out of error reporting via
 * `bestEffortQueryMeta`. `QueryMeta` values are `unknown`, so this checks for
 * the literal `false` — an absent or malformed flag keeps reporting on.
 */
export const isErrorReportingOptedOut = (query: Pick<Query, 'meta'>): boolean =>
  query.meta?.reportErrors === false;

/**
 * The reporting decision behind the global `QueryCache.onError`
 * (src/utils/trpc.ts) — kept here so it is unit-testable without stubbing the
 * Sentry transport.
 *
 * Reports by default. A **fetch-level abort** is suppressed only when the
 * teardown is attributable to us rather than to the user's connection:
 *
 *  - **nobody wants the data any more** — `isActive()` is false when the query
 *    has no observers (unmounted, #7723), when every observer has `enabled:
 *    false`, or when it was removed from the cache. All three are our own
 *    cancellation, so the resulting abort is noise.
 *  - **the document is going away** — a cross-document navigation kills every
 *    in-flight request while components stay mounted and enabled, so
 *    `isActive()` can't see it (see `isPageUnloading`).
 *
 * Anything else reports, including a fetch abort on a live, enabled query with
 * no unload in progress — that is the genuine-connectivity signal this handler
 * exists for. A non-fetch failure (a real 4xx/5xx) always reports.
 *
 * A query carrying `bestEffortQueryMeta` is exempt from all of the above: it
 * opts out of reporting entirely, not just for aborts.
 *
 * Widened from a bare observer count in COI-716 — the count alone missed every
 * still-mounted case, which was ~all of RISKSMART-APP-B5P.
 */
export const shouldReportTrpcQueryError = (
  error: unknown,
  query: Pick<Query, 'isActive' | 'meta'>
): boolean => {
  if (isErrorReportingOptedOut(query)) {
    return false;
  }

  if (!isTrpcFetchAbortError(error)) {
    return true;
  }

  return query.isActive() && !isPageUnloading();
};

/**
 * tRPC twin of `isPermissionError` (src/utils/graphqlUtils.ts). The Apollo
 * path surfaces a denied action as a GraphQL error with
 * `extensions.code === 'permission-error'`; the tRPC path surfaces the same
 * denial as a `TRPCClientError` whose formatted `code` is `'FORBIDDEN'`
 * (the data-layer returns 403 for a non-author edit; the tRPC service maps
 * 403 → FORBIDDEN).
 *
 * The formatted code lives on `error.data.code` — the same accessor
 * `mapFormError` (src/components/form/form/formError.ts) reads. `error.shape`
 * carries the raw formatter output; check `shape.data.code` too so the helper
 * still fires if `data` is momentarily unpopulated.
 *
 * Used by the comments editor's `tinycomments_edit_comment` handler
 * (RSP-6496) to treat a non-author edit as `canEdit: false` under EITHER data
 * path behind the `trpc` flag.
 */
export const isTrpcPermissionError = (e: unknown): boolean => {
  if (!isTRPCClientError(e)) {
    return false;
  }

  return e.data?.code === 'FORBIDDEN' || e.shape?.data?.code === 'FORBIDDEN';
};

export const mapTRPCRefetch = async <TTRPCData, TGraphQLQuery>(
  trpcRefetch: (options?: RefetchOptions) => Promise<
    QueryObserverResult<
      TTRPCData,
      TRPCClientErrorLike<{
        transformer: true;
        errorShape: unknown;
      }>
    >
  >,
  trpcToGraphQLMapper: (data: TTRPCData) => TGraphQLQuery
) => {
  const { data: refetchData, error } = await trpcRefetch();

  const mappedRefetchData = refetchData
    ? trpcToGraphQLMapper(refetchData)
    : undefined;

  return { data: mappedRefetchData, error: error ?? null };
};
